import type { AvlItem } from '@/services/approvedVendorList';
import { isErrorWithData } from '@/services/helpers';
import {
  PalmettoButton,
  PalmettoIcon,
  PalmettoModal,
  PalmettoSelect,
  PalmettoTag,
  PalmettoTooltip,
} from '@palmetto/dsm-components';
import { DateInput, toast } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import type {
  Control,
  FieldPath,
  FieldValues,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form';

import {
  ApprovedVendorAllocation,
  avlTimelineStart,
  type ApprovedVendorItemDocument,
  type BulkUpdateApprovedVendorItemsRequest,
  type BulkUpdateApprovedVendorItemsResponse,
  type IsoDateString,
} from 'types';

import { ApprovedVendorAllocationLabels, ApprovedVendorAllocationTagColors } from './allocationLabels';
import { formatWindowDate, sortByStartDateAsc } from './AllocationScheduleSection';
import { computeSharedFieldSpecs, type SharedFieldSpec } from './avlBulkEditFieldComputation';
import { formatHistoryValue, type FieldSpec } from './avlFieldRegistry';
import { AvlFieldRenderer } from './AvlItemFormFields';

// Same top-3-plus-"+K more" truncation used for the header's selected-model-number chips and each
// mixed-value field's pills, so both read consistently.
const MAX_VISIBLE_CHIPS = 3;
// Tooltip lists up to this many distinct values total before truncating itself.
const MAX_TOOLTIP_VALUES = 10;

const allocationStatusOptions = Object.values(ApprovedVendorAllocation).map((value) => ({
  value,
  label: ApprovedVendorAllocationLabels[value],
}));

interface AllocationCount {
  status: ApprovedVendorAllocation;
  count: number;
}

const countCurrentAllocations = (items: AvlItem[]): AllocationCount[] => {
  const counts = new Map<ApprovedVendorAllocation, number>();
  for (const item of items) {
    counts.set(item.allocation, (counts.get(item.allocation) ?? 0) + 1);
  }
  return Array.from(counts.entries()).map(([status, count]) => ({ status, count }));
};

/** A new window must start after this. */
const getLatestWindowStartDate = (items: AvlItem[]): string => {
  const rows = sortByStartDateAsc(
    items.flatMap((item) => (item.allocationDetails ?? []).filter((row) => !row.archived)),
  );
  return rows.length > 0 ? rows[rows.length - 1].startDate : avlTimelineStart;
};

/** Converts a calendar-day `Date` (as picked by the legacy `DateInput`) to an `IsoDateString`. */
const toIsoDateString = (date: Date): IsoDateString => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}` as IsoDateString;
};

const buildTooltipContent = (values: string[]): string => {
  const shown = values.slice(0, MAX_TOOLTIP_VALUES);
  const remaining = values.length - shown.length;
  return remaining > 0 ? `${shown.join(', ')}, ...and ${remaining} more` : shown.join(', ');
};

interface ChipOverflowListProps {
  values: string[];
  overflowAriaLabel: string;
}

/** Top-3 visible chips; anything beyond that collapses into a "+K more" chip with a tooltip. */
const ChipOverflowList = ({ values, overflowAriaLabel }: ChipOverflowListProps) => {
  const visible = values.slice(0, MAX_VISIBLE_CHIPS);
  const hidden = values.slice(MAX_VISIBLE_CHIPS);

  return (
    <div className="flex flex-row flex-wrap items-center gap-4">
      {visible.map((value, index) => (
        // Displayed values aren't guaranteed unique (e.g. two items sharing a mixed field value).
        <PalmettoTag key={`${value}-${index}`} label={value} />
      ))}
      {hidden.length > 0 && (
        <PalmettoTooltip content={buildTooltipContent(values)} position="top-center">
          <button type="button" aria-label={overflowAriaLabel}>
            <PalmettoTag label={`+${hidden.length}`} />
          </button>
        </PalmettoTooltip>
      )}
    </div>
  );
};

interface MixedFieldValuesProps {
  spec: FieldSpec;
  values: unknown[];
}

const MixedFieldValues = ({ spec, values }: MixedFieldValuesProps) => {
  const displayValues = useMemo(
    () => values.map((value) => formatHistoryValue(spec.name, value)),
    [values, spec.name],
  );

  return (
    <ChipOverflowList
      values={displayValues}
      overflowAriaLabel={`${values.length - MAX_VISIBLE_CHIPS} more ${spec.label} values`}
    />
  );
};

/** Scoped subscription: a keystroke re-renders only this row, not the modal owner. */
const useFieldIsDirty = (control: Control<FieldValues>, name: string): boolean => {
  const { dirtyFields } = useFormState({ control, name: name as FieldPath<FieldValues> });
  return !!dirtyFields[name as keyof typeof dirtyFields];
};

interface MixedFieldRowProps {
  spec: FieldSpec;
  values: unknown[];
  control: Control<FieldValues>;
  isActivated: boolean;
  onActivate: () => void;
  onCancel: () => void;
}

const MixedFieldRow = ({ spec, values, control, isActivated, onActivate, onCancel }: MixedFieldRowProps) => (
  <div className="col-span-2 flex flex-col gap-4">
    <div className="flex items-center justify-between">
      <span className="body-bold-300 text-text-secondary">{spec.label}</span>
      {isActivated ? (
        <PalmettoButton
          type="button"
          emphasis="tertiary"
          size="xs"
          layout="hug"
          aria-label={`Revert ${spec.label}`}
          onClick={onCancel}
        >
          <PalmettoIcon name="returnBack" size="sm" />
        </PalmettoButton>
      ) : (
        <PalmettoButton
          type="button"
          emphasis="tertiary"
          size="xs"
          layout="hug"
          aria-label={`Set ${spec.label} for all items`}
          onClick={onActivate}
        >
          <PalmettoIcon name="penEdit" size="sm" />
        </PalmettoButton>
      )}
    </div>
    <MixedFieldValues spec={spec} values={values} />
    {isActivated && <AvlFieldRenderer<FieldValues> spec={spec} control={control} />}
  </div>
);

interface FieldEditedRowProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  resetField: UseFormResetField<FieldValues>;
}

const FieldEditedRow = ({ control, name, label, resetField }: FieldEditedRowProps) => {
  const isDirty = useFieldIsDirty(control, name);
  const handleRevert = useCallback(() => resetField(name as FieldPath<FieldValues>), [resetField, name]);

  if (!isDirty) return null;

  return (
    <div className="flex items-center justify-end">
      <PalmettoButton
        type="button"
        emphasis="tertiary"
        size="xs"
        layout="hug"
        aria-label={`Revert ${label}`}
        onClick={handleRevert}
      >
        <PalmettoIcon name="returnBack" size="sm" />
      </PalmettoButton>
    </div>
  );
};

interface EligibleFieldRowProps {
  spec: FieldSpec;
  values: unknown[];
  control: Control<FieldValues>;
  resetField: UseFormResetField<FieldValues>;
  isActivated: boolean;
  onActivate: (spec: FieldSpec) => void;
  onDeactivate: (fieldName: string) => void;
}

const EligibleFieldRow = ({
  spec,
  values,
  control,
  resetField,
  isActivated,
  onActivate,
  onDeactivate,
}: EligibleFieldRowProps) => {
  const handleActivate = useCallback(() => onActivate(spec), [onActivate, spec]);
  const handleDeactivate = useCallback(() => onDeactivate(spec.name), [onDeactivate, spec.name]);

  if (spec.kind === 'section') {
    return <AvlFieldRenderer<FieldValues> spec={spec} control={control} />;
  }

  const isMixed = values.length > 1;
  if (isMixed) {
    return (
      <MixedFieldRow
        spec={spec}
        values={values}
        control={control}
        isActivated={isActivated}
        onActivate={handleActivate}
        onCancel={handleDeactivate}
      />
    );
  }

  // Full width via AvlFieldRenderer; aliases is bulk-edit-excluded so it never reaches here.
  const spansTwoColumns = spec.kind === 'textarea' || spec.kind === 'size' || spec.kind === 'feocData';

  return (
    <div className={spansTwoColumns ? 'col-span-2 flex flex-col gap-4' : 'flex flex-col gap-4'}>
      <FieldEditedRow control={control} name={spec.name} label={spec.label} resetField={resetField} />
      <AvlFieldRenderer<FieldValues> spec={spec} control={control} />
    </div>
  );
};

/** Placeholder default value per field kind for a mixed (disagreeing) field — never a real value. */
const emptyDefaultFor = (spec: FieldSpec): unknown => {
  switch (spec.kind) {
    case 'multiselect':
    case 'feocData':
      return [];
    default:
      return undefined;
  }
};

const buildDefaultValues = (fieldSpecs: SharedFieldSpec[]): FieldValues => {
  const defaults: Record<string, unknown> = {};
  for (const { spec, values } of fieldSpecs) {
    if (spec.kind === 'section') continue;
    defaults[spec.name] = values.length === 1 ? values[0] : emptyDefaultFor(spec);
  }
  return defaults;
};

/** Extracts item ids from the bracketed `[id1, id2]` list in the server's allocation-conflict message. */
const parseConflictingIds = (message: string): string[] => {
  const match = message.match(/\[([^\]]*)\]/);
  if (!match) return [];
  return match[1]
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

interface AllocationWindowFieldsProps {
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
}

// Isolates re-renders here so typing doesn't retrigger the modal owner's FocusTrap.
const AllocationWindowFields = ({ control, setValue }: AllocationWindowFieldsProps) => {
  const allocError = useWatch({ control, name: 'allocError' }) as string | undefined;

  return (
    <>
      <Controller
        name="allocStartDate"
        control={control}
        render={({ field }) => (
          <DateInput
            id="bulk-allocation-start-date"
            name="bulkAllocationStartDate"
            label="Start Date"
            placeholder="MM-DD-YYYY"
            value={field.value ? (field.value as Date).toLocaleDateString('en-US') : ''}
            error={allocError}
            datePickerProps={{
              onChange: (date: Date | [Date, Date] | null) => {
                field.onChange(Array.isArray(date) ? date[0] : date);
                setValue('allocError', undefined);
              },
              selected: (field.value as Date) || undefined,
            }}
            textInputProps={{}}
          />
        )}
      />
      <Controller
        name="allocStatus"
        control={control}
        render={({ field }) => (
          <PalmettoSelect
            label="Allocation Status"
            items={allocationStatusOptions.map((opt) => ({ ...opt, selected: opt.value === field.value }))}
            onValueChange={(details: { value: string[] }) => {
              field.onChange((details.value[0] as ApprovedVendorAllocation) ?? '');
              setValue('allocError', undefined);
            }}
          />
        )}
      />
    </>
  );
};

type DialogStep = 'edit' | 'review' | 'result';

type BulkEditResult =
  | { type: 'partial'; response: BulkUpdateApprovedVendorItemsResponse }
  | { type: 'rejected'; message: string; ids: string[] };

interface ReviewStepContentProps {
  control: Control<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  sharedFieldSpecs: SharedFieldSpec[];
  hasAllocationDetail: boolean;
  allocStartDate: Date | null;
  allocStatus: ApprovedVendorAllocation | '';
  itemsCount: number;
}

const ReviewStepContent = ({
  control,
  getValues,
  sharedFieldSpecs,
  hasAllocationDetail,
  allocStartDate,
  allocStatus,
  itemsCount,
}: ReviewStepContentProps) => {
  const { dirtyFields } = useFormState({ control });
  const formValues = getValues();
  const reviewLines = Object.keys(dirtyFields)
    .map((fieldName) => sharedFieldSpecs.find((entry) => entry.spec.name === fieldName))
    .filter((entry): entry is SharedFieldSpec => !!entry)
    .map((entry) => ({
      fieldName: entry.spec.name,
      label: entry.spec.label,
      displayValue: formatHistoryValue(entry.spec.name, formValues[entry.spec.name]),
    }));

  return (
    <div className="flex flex-col gap-16">
      <h2>Review changes</h2>
      {reviewLines.length === 0 && !hasAllocationDetail && (
        <p className="text-text-secondary">No changes to apply.</p>
      )}
      <ul className="flex flex-col gap-8">
        {reviewLines.map((line) => (
          <li key={line.fieldName} className="body-300 text-text-primary">
            <strong>{line.label}</strong>
            {` → ${line.displayValue} · applies to ${itemsCount} item${itemsCount === 1 ? '' : 's'}`}
          </li>
        ))}
        {hasAllocationDetail && allocStartDate && allocStatus && (
          <li className="body-300 text-text-primary">
            <strong>Allocation window</strong>
            {` → starting ${formatWindowDate(toIsoDateString(allocStartDate))}: ${ApprovedVendorAllocationLabels[allocStatus]} · applies to ${itemsCount} item${itemsCount === 1 ? '' : 's'}`}
          </li>
        )}
      </ul>
      {hasAllocationDetail && (
        <p className="body-bold-300 text-text-primary">
          Allocation windows can&apos;t be edited or removed once saved.
        </p>
      )}
    </div>
  );
};

export interface ApprovedVendorBulkEditDialogProps {
  items: AvlItem[];
  onClose: () => void;
  onSubmit: (payload: BulkUpdateApprovedVendorItemsRequest) => Promise<BulkUpdateApprovedVendorItemsResponse>;
}

export const ApprovedVendorBulkEditDialog = ({ items, onClose, onSubmit }: ApprovedVendorBulkEditDialogProps) => {
  const sharedFieldSpecs = useMemo(() => computeSharedFieldSpecs(items), [items]);
  const currentAllocationCounts = useMemo(() => countCurrentAllocations(items), [items]);
  const latestWindowStartDate = useMemo(() => getLatestWindowStartDate(items), [items]);
  // `defaultValues` is only ever read once by useForm at mount; the selection is fixed for the
  // life of an open dialog, so recomputing it on every render (via useMemo) is safe but unused
  // after the initial render.
  const defaultValues = useMemo(() => buildDefaultValues(sharedFieldSpecs), [sharedFieldSpecs]);

  // Don't read `formState` here: re-rendering the modal owner makes its FocusTrap steal focus.
  const { control, getValues, resetField, getFieldState, setValue, setError, clearErrors } = useForm<FieldValues>({
    defaultValues,
  });

  const [step, setStep] = useState<DialogStep>('edit');
  const [activatedFields, setActivatedFields] = useState<ReadonlySet<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<BulkEditResult | null>(null);

  const modelNumberById = useMemo(() => new Map(items.map((item) => [item.id, item.modelNumber])), [items]);
  const modelNumberFor = useCallback((id: string) => modelNumberById.get(id) ?? id, [modelNumberById]);

  const handleActivateField = useCallback(
    (spec: FieldSpec) => {
      setActivatedFields((prev) => new Set(prev).add(spec.name));
      // Explicit false on activate makes an untouched checkbox mean "No", not "no change".
      if (spec.kind === 'checkbox') {
        setValue(spec.name, false, { shouldDirty: true });
      }
    },
    [setValue],
  );

  const handleDeactivateField = useCallback(
    (fieldName: string) => {
      resetField(fieldName);
      setActivatedFields((prev) => {
        const next = new Set(prev);
        next.delete(fieldName);
        return next;
      });
    },
    [resetField],
  );

  const getDirtyFieldNames = useCallback(
    () =>
      sharedFieldSpecs
        .filter(({ spec }) => spec.kind !== 'section' && getFieldState(spec.name).isDirty)
        .map(({ spec }) => spec.name),
    [sharedFieldSpecs, getFieldState],
  );

  const handleNext = useCallback(() => {
    const values = getValues();
    const nextAllocStartDate = (values.allocStartDate as Date | undefined) ?? null;
    const nextAllocStatus = (values.allocStatus as ApprovedVendorAllocation | undefined) ?? '';

    if (!!nextAllocStartDate !== !!nextAllocStatus) {
      setValue('allocError', 'Start date and status are both required to add an allocation window.');
      return;
    }
    if (nextAllocStartDate && nextAllocStatus) {
      const isoStartDate = toIsoDateString(nextAllocStartDate);
      if (isoStartDate < avlTimelineStart) {
        setValue('allocError', `Start date must be on or after ${avlTimelineStart}.`);
        return;
      }
    }

    clearErrors('size');
    const size = values.size as { value?: number; unit?: string } | undefined;
    if (getFieldState('size').isDirty && (size?.value !== undefined && size?.value !== null) !== !!size?.unit) {
      setError('size', { type: 'manual', message: 'Enter both a value and a unit' });
      return;
    }

    if (getDirtyFieldNames().length === 0 && !(nextAllocStartDate && nextAllocStatus)) {
      toast.error('Make at least one change before continuing.');
      return;
    }
    setStep('review');
  }, [getValues, setValue, clearErrors, getFieldState, setError, getDirtyFieldNames]);

  const handleBack = useCallback(() => setStep('edit'), []);

  const handleConfirm = useCallback(async () => {
    const values = getValues();
    const changes: Record<string, unknown> = {};
    for (const fieldName of getDirtyFieldNames()) {
      changes[fieldName] = values[fieldName];
    }

    const confirmAllocStartDate = (values.allocStartDate as Date | undefined) ?? null;
    const confirmAllocStatus = (values.allocStatus as ApprovedVendorAllocation | undefined) ?? '';

    const payload: BulkUpdateApprovedVendorItemsRequest = {
      ids: items.map((item) => item.id),
      changes: changes as Partial<ApprovedVendorItemDocument>,
      ...(confirmAllocStartDate && confirmAllocStatus
        ? {
            newAllocationDetail: {
              startDate: toIsoDateString(confirmAllocStartDate),
              allocationStatus: confirmAllocStatus,
            },
          }
        : {}),
    };

    setIsSubmitting(true);
    try {
      const response = await onSubmit(payload);
      if (response.failed.length === 0) {
        toast.success(`Updated ${response.updated.length} item${response.updated.length === 1 ? '' : 's'}`);
        onClose();
        return;
      }
      setResult({ type: 'partial', response });
      setStep('result');
    } catch (error) {
      console.error('Error applying bulk edit:', error);
      const message = isErrorWithData(error) ? error.data.message : 'Failed to apply bulk edit. Please try again.';
      setResult({ type: 'rejected', message, ids: parseConflictingIds(message) });
      setStep('result');
    } finally {
      setIsSubmitting(false);
    }
  }, [getValues, getDirtyFieldNames, items, onSubmit, onClose]);

  // Unsubscribed read for the review step so editing on 'edit' never re-renders the modal owner.
  const reviewValues = step === 'review' ? getValues() : undefined;
  const reviewAllocStartDate = (reviewValues?.allocStartDate as Date | undefined) ?? null;
  const reviewAllocStatus = (reviewValues?.allocStatus as ApprovedVendorAllocation | undefined) ?? '';

  const bottomContent =
    step === 'edit' ? (
      <div className="flex justify-end gap-8">
        <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={onClose}>
          Cancel
        </PalmettoButton>
        <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleNext}>
          Next
        </PalmettoButton>
      </div>
    ) : step === 'review' ? (
      <div className="flex justify-end gap-8">
        <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleBack} disabled={isSubmitting}>
          Back
        </PalmettoButton>
        <PalmettoButton
          type="button"
          emphasis="primary"
          layout="hug"
          onClick={handleConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Applying...' : 'Confirm'}
        </PalmettoButton>
      </div>
    ) : (
      <div className="flex justify-end gap-8">
        <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={onClose}>
          Close
        </PalmettoButton>
      </div>
    );

  return (
    <PalmettoModal open onClose={onClose} invertedBackground={false} bottomContent={bottomContent}>
      {step === 'edit' && (
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-4">
            <h2>{`Editing ${items.length} items`}</h2>
            <ChipOverflowList
              values={items.map((item) => item.modelNumber)}
              overflowAriaLabel={`${Math.max(items.length - MAX_VISIBLE_CHIPS, 0)} more selected items`}
            />
          </div>

          <div className="grid grid-cols-2 gap-x-16 gap-y-8">
            {sharedFieldSpecs.map(({ spec, values }) => (
              <EligibleFieldRow
                key={spec.name}
                spec={spec}
                values={values}
                control={control}
                resetField={resetField}
                isActivated={activatedFields.has(spec.name)}
                onActivate={handleActivateField}
                onDeactivate={handleDeactivateField}
              />
            ))}
          </div>

          <div className="flex flex-col gap-8 border-t border-border-default pt-16">
            <h3 className="heading-100 text-text-primary">New allocation window</h3>
            <p className="body-200 text-text-secondary">
              Applies to every selected item. Allocation windows can&apos;t be edited or removed once saved.
            </p>
            <div className="flex flex-col gap-4">
              <span className="body-300 text-text-secondary">Current allocation</span>
              <div className="flex flex-row flex-wrap items-center gap-8">
                {currentAllocationCounts.map(({ status, count }) => (
                  <PalmettoTag
                    key={status}
                    label={`${ApprovedVendorAllocationLabels[status]} · ${count}`}
                    color={ApprovedVendorAllocationTagColors[status]}
                  />
                ))}
              </div>
              <span className="body-300 text-text-secondary">
                {`Latest window starts: ${formatWindowDate(latestWindowStartDate)}`}
              </span>
            </div>
            <AllocationWindowFields control={control} setValue={setValue} />
          </div>
        </div>
      )}

      {step === 'review' && (
        <ReviewStepContent
          control={control}
          getValues={getValues}
          sharedFieldSpecs={sharedFieldSpecs}
          hasAllocationDetail={!!reviewAllocStartDate && !!reviewAllocStatus}
          allocStartDate={reviewAllocStartDate}
          allocStatus={reviewAllocStatus}
          itemsCount={items.length}
        />
      )}

      {step === 'result' && result && (
        <div className="flex flex-col gap-16">
          {result.type === 'partial' && (
            <>
              <h2>Bulk edit results</h2>
              <p className="text-text-primary">
                {`${result.response.updated.length} item${result.response.updated.length === 1 ? '' : 's'} updated successfully.`}
              </p>
              {result.response.failed.length > 0 && (
                <div className="flex flex-col gap-4">
                  <p className="body-bold-300 text-text-primary">The following items could not be updated:</p>
                  <ul className="flex flex-col gap-4">
                    {result.response.failed.map((failure) => (
                      <li key={failure.id} role="alert" className="body-300 text-text-error">
                        {`${modelNumberFor(failure.id)}: ${failure.error}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          {result.type === 'rejected' && (
            <>
              <h2>Bulk edit rejected</h2>
              <p role="alert" className="body-300 text-text-error">
                {result.message}
              </p>
              {result.ids.length > 0 && (
                <div className="flex flex-col gap-4">
                  <p className="body-bold-300 text-text-primary">Conflicting items:</p>
                  <ul className="flex flex-col gap-4">
                    {result.ids.map((id) => (
                      <li key={id} className="body-300 text-text-primary">
                        {modelNumberFor(id)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </PalmettoModal>
  );
};
